import { Link, useNavigate } from 'react-router-dom';

import { Avatar, TableCell, TableRow, Typography } from '@/ui';
import { toDateTimeString } from '@/utils';

import type { ManageCoach } from '@/store/api/userApi';
import type { User } from '@/types';
import type { FC } from 'react';

import CalCard from '@/components/Coaches/List/CalRow';
import { coachStatusDisplay, sortCoachStatuses } from '@/constants/coachStatus';
import { getMockExtras } from '@/pages/Manage/mockExtrasRef';

const IS_LOCAL = import.meta.env.VITE_APP_ENV === 'local';

const formatDate = (dateStr?: string | null): string => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
    year: 'numeric',
  });
};

const Row: FC<ManageCoach> = ({
  activeChemCalls: apiActiveChemCalls,
  avatarUrl = '',
  coachProfile,
  coachStatus,
  coachStatuses,
  coachees,
  email = '',
  firstName,
  fullName,
  id,
  lastName,
  membersCount: apiMembersCount,
  updatedAt,
}) => {
  const navigate = useNavigate();
  const legacyStatuses = coachStatus ? [coachStatus] : [];
  const statuses = sortCoachStatuses(
    coachStatuses?.length ? coachStatuses : legacyStatuses,
  );
  const missingStatusDisplay = coachStatusDisplay(null);
  const extras = IS_LOCAL ? getMockExtras().get(id) : undefined;
  // Prefer mock extras in local; in production prefer explicit aggregate fields
  // from view=manage_coaches, falling back to legacy nested coachees if present.
  // NOTE: the API response converter (axios-case-converter) camelizes nested
  // JSONB keys, so seat_type_rates.standard_leadership arrives as
  // seatTypeRates.standardLeadership. Read rates by their camelized keys.
  const seatTypeRates = coachProfile?.seatTypeRates as
    Partial<Record<string, number | null>> | undefined;
  const memberCount =
    extras?.members ??
    apiMembersCount ??
    (coachees as User[] | undefined)?.length;
  const members = memberCount ?? 0;
  const activeChemCalls = extras?.activeChemCalls ?? apiActiveChemCalls;
  const standardRate =
    extras?.standardRate ?? seatTypeRates?.standardLeadership;
  const executiveRate = extras?.executiveRate ?? seatTypeRates?.executive;
  const capacity = coachProfile?.capacity ?? 0;
  const isNearCapacity = capacity > 0 && members > 0 && members >= capacity - 1;

  const chemCount = activeChemCalls ?? 0;
  const membersPct =
    capacity > 0 ? Math.min((members / capacity) * 100, 100) : 0;
  const chemPct =
    capacity > 0 ? Math.min((chemCount / capacity) * 100, 100 - membersPct) : 0;
  const totalPct = membersPct + chemPct;
  const hasChemCalls = chemPct > 0;

  const onClick = () => navigate(`/coach/manage/coaches/${id}`);

  return (
    <TableRow className="hover:bg-sage-100" onClick={onClick}>
      <TableCell>
        <div className="flex items-center gap-2">
          <Avatar firstName={firstName} lastName={lastName} src={avatarUrl} />
          <Typography variant="subtitle2">{fullName}</Typography>
        </div>
      </TableCell>
      <TableCell>{coachProfile?.focus}</TableCell>
      <TableCell>{email}</TableCell>
      <TableCell className="!whitespace-nowrap">
        {statuses.length === 0 ? (
          <Link
            className={`inline-block rounded-full border-0 px-2 py-0.5 text-xs font-medium ${missingStatusDisplay.badgeClassName}`}
            to={`/coach/manage/coaches/${id}`}
            onClick={(event) => event.stopPropagation()}
          >
            {missingStatusDisplay.label}
          </Link>
        ) : (
          <div className="flex flex-wrap gap-1">
            {statuses.map((status) => {
              const statusDisplay = coachStatusDisplay(status);
              return (
                <span
                  key={status}
                  className={`inline-block rounded-full border-0 px-2 py-0.5 text-xs font-medium ${statusDisplay.badgeClassName}`}
                >
                  {statusDisplay.label}
                </span>
              );
            })}
          </div>
        )}
      </TableCell>
      <TableCell>{activeChemCalls ?? '—'}</TableCell>
      <TableCell>
        <span className={isNearCapacity ? 'font-semibold text-red-600' : ''}>
          {memberCount ?? '—'}
        </span>
      </TableCell>
      <TableCell>{capacity}</TableCell>
      <TableCell style={{ minWidth: 280 }}>
        {capacity > 0 ? (
          <div
            className="group relative flex items-center gap-2"
            title={`Active Chem Calls - ${activeChemCalls ?? '—'}, Current Members - ${memberCount ?? '—'}, Capacity - ${capacity}`}
          >
            <div className="relative h-3 w-full min-w-[180px] rounded-full bg-cement-100">
              {hasChemCalls && (
                <div
                  className="absolute left-0 top-0 h-full rounded-l-full bg-amber-400"
                  style={{ width: `${chemPct}%` }}
                />
              )}
              <div
                className={`absolute top-0 h-full bg-sage-500 ${!hasChemCalls ? 'rounded-l-full' : ''}`}
                style={{
                  borderBottomRightRadius: totalPct >= 99 ? '9999px' : 0,
                  borderTopRightRadius: totalPct >= 99 ? '9999px' : 0,
                  left: `${chemPct}%`,
                  width: `${membersPct}%`,
                }}
              />
            </div>
            <span
              className={`whitespace-nowrap text-sm ${isNearCapacity ? 'font-semibold text-red-600' : 'text-cement-500'}`}
            >
              {activeChemCalls ?? '—'}/{memberCount ?? '—'}/{capacity}
            </span>
          </div>
        ) : (
          <span className="text-xs text-cement-400">—</span>
        )}
      </TableCell>
      <TableCell style={{ paddingLeft: 24 }}>
        {standardRate ?? '—'}/{executiveRate ?? '—'}
      </TableCell>
      <TableCell className="hidden lg:table-cell">
        <CalCard
          schedulingProvider={coachProfile?.schedulingProvider}
          username={coachProfile?.calendarUrl ?? ''}
        />
      </TableCell>
      <TableCell className="hidden lg:table-cell">
        {formatDate(coachProfile?.startDate)}
      </TableCell>
      <TableCell className="hidden lg:table-cell">
        {toDateTimeString(new Date(updatedAt))}
      </TableCell>
    </TableRow>
  );
};

export default Row;
